/**
 * SSOT for "פרטי טיסות נבחרות" on LDS package order details:
 * primary — selectedFlightOptionFromUser.linkedFlights
 * fallback — order.linkedflights / order.linkedFlights
 * enrichment — order_response.reservation.flight when leg fields are missing
 */
import { safeOrderResponseObject } from "./packageOrderCardSummary";
import {
  buildFlightOptionSignature,
  normalizeFlightSelectionSignature,
} from "../vacationPackageFlightSelectionSSOT";

function parseMaybeJson(value) {
  if (value == null) return null;
  if (typeof value === "object") return value;
  if (typeof value === "string" && value.trim()) {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return null;
}

/** Linked flights object stored on lds_orders row only (not order_response). */
export function getLdsOrderLinkedFlightsFromRow(order) {
  if (!order || typeof order !== "object") return null;
  const lf =
    parseMaybeJson(order.linkedflights) ??
    parseMaybeJson(order.linkedFlights) ??
    (order.linkedflights && typeof order.linkedflights === "object" ? order.linkedflights : null) ??
    (order.linkedFlights && typeof order.linkedFlights === "object" ? order.linkedFlights : null);
  if (!lf || typeof lf !== "object" || Array.isArray(lf)) return null;
  return lf;
}

function getSelectedFlightOptionFromOrder(order) {
  if (!order || typeof order !== "object") return null;
  const ext = parseMaybeJson(order.extended);
  const fromExt =
    ext && typeof ext === "object" ? ext.selectedFlightOptionFromUser : null;
  if (fromExt && typeof fromExt === "object") return fromExt;
  const lf = getLdsOrderLinkedFlightsFromRow(order);
  if (lf?.selectedFlightOptionFromUser && typeof lf.selectedFlightOptionFromUser === "object") {
    return lf.selectedFlightOptionFromUser;
  }
  return order.selectedFlightOptionFromUser ?? null;
}

function isDevRuntime() {
  try {
    return Boolean(import.meta?.env?.DEV);
  } catch {
    return false;
  }
}

export function logOrderDetailsFlightSsotTrace(order, sourceUsed, extra = {}) {
  if (!isDevRuntime()) return;
  try {
    const selected = getSelectedFlightOptionFromOrder(order);
    const lf = getLdsOrderLinkedFlightsFromRow(order);
    const selectedSignature = selected?.linkedFlights
      ? normalizeFlightSelectionSignature(buildFlightOptionSignature(selected.linkedFlights))
      : null;
    const linkedFlightsSignature = lf
      ? normalizeFlightSelectionSignature(buildFlightOptionSignature(lf))
      : null;
    console.info("[LDS_ORDER_DETAILS_FLIGHT_SSOT_TRACE]", {
      orderId: order?.id ?? order?.ID ?? null,
      bookingRef: order?.orderid ?? order?.order_id ?? null,
      sourceUsed,
      selectedSignature,
      linkedFlightsSignature,
      decodedRoomTokenSignature: null,
      driftDetectedForDisplayOnly: Boolean(
        selectedSignature &&
          linkedFlightsSignature &&
          selectedSignature !== linkedFlightsSignature
      ),
      ...extra,
    });
  } catch {
    /* ignore */
  }
}

/**
 * True when the persisted row has a non-empty linkedFlights payload (flight list or other keys).
 * Used to avoid showing this card for hotel-only / unrelated orders.
 */
export function hasNonEmptyLdsOrderLinkedFlightsBlob(order) {
  const lf = getLdsOrderLinkedFlightsFromRow(order);
  if (!lf) return false;
  const flights = lf.flight ?? lf.flights;
  if (Array.isArray(flights) && flights.length > 0) return true;
  if (flights && typeof flights === "object" && !Array.isArray(flights)) return true;
  return Object.keys(lf).length > 0;
}

function normalizeFlightLegValue(v) {
  if (v == null) return "";
  const s = String(v).trim();
  if (!s) return "";
  const paren = s.match(/\(([A-Z]{3})\)/);
  if (paren?.[1]) return paren[1];
  const code = s.match(/\b[A-Z]{3}\b/);
  if (code?.[0]) return code[0];
  return s;
}

function depCodeFromLeg(leg) {
  if (!leg || typeof leg !== "object") return "";
  return normalizeFlightLegValue(
    leg.depAirport?.code ??
      leg.departureAirport?.code ??
      leg.origin ??
      leg.from ??
      leg.depCity ??
      leg.departure?.airportCode ??
      leg.depAirportCode ??
      leg.departureAirportCode
  );
}

function arrCodeFromLeg(leg) {
  if (!leg || typeof leg !== "object") return "";
  return normalizeFlightLegValue(
    leg.arrAirport?.code ??
      leg.arrivalAirport?.code ??
      leg.destination ??
      leg.to ??
      leg.arrCity ??
      leg.arrival?.airportCode ??
      leg.arrAirportCode ??
      leg.arrivalAirportCode
  );
}

function legsFromFlightItem(item) {
  if (!item || typeof item !== "object") return [];
  const raw = item.leg ?? item.legs;
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (raw && typeof raw === "object") return [raw];
  return [];
}

function flightArrayFromLinked(lf) {
  if (!lf || typeof lf !== "object") return [];
  const inner =
    lf.linkedFlights && typeof lf.linkedFlights === "object" && !Array.isArray(lf.linkedFlights)
      ? lf.linkedFlights
      : lf;
  const raw = inner.flight ?? inner.flights;
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (raw && typeof raw === "object") return [raw];
  return [];
}

function flightArrayFromReservation(reservation) {
  if (!reservation || typeof reservation !== "object") return [];
  const raw = reservation.flight ?? reservation.flights;
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (raw && typeof raw === "object") return [raw];
  return [];
}

function pickStr(...vals) {
  for (const v of vals) {
    if (v == null) continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return "";
}

function mergeLeg(primary, fallback) {
  if (!primary || typeof primary !== "object") return fallback && typeof fallback === "object" ? fallback : primary;
  if (!fallback || typeof fallback !== "object") return primary;
  const out = { ...fallback, ...primary };
  return out;
}

function firstLastFromLegs(legs) {
  if (!Array.isArray(legs) || !legs.length) return { first: null, last: null };
  return { first: legs[0], last: legs[legs.length - 1] };
}

function formatDateHe(isoLike) {
  const s = pickStr(isoLike);
  if (!s) return "";
  const d = s.includes("T") ? s.split("T")[0] : s.length >= 10 ? s.slice(0, 10) : s;
  const m = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return d;
}

function formatTimeHe(isoLike) {
  const s = pickStr(isoLike);
  if (!s) return "";
  if (s.includes("T")) {
    const part = s.split("T")[1] || "";
    const hm = part.match(/(\d{1,2}):(\d{2})/);
    if (hm) return `${String(parseInt(hm[1], 10)).padStart(2, "0")}:${hm[2]}`;
  }
  const hm2 = s.match(/(\d{1,2}):(\d{2})/);
  if (hm2) return `${String(parseInt(hm2[1], 10)).padStart(2, "0")}:${hm2[2]}`;
  return "";
}

function depDateTimeCandidates(leg) {
  return [leg?.depDate, leg?.departureDateTime, leg?.departureDate, leg?.departureLocal, leg?.departure];
}

function arrDateTimeCandidates(leg) {
  return [leg?.arrDate, leg?.arrivalDateTime, leg?.arrivalDate, leg?.arrivalLocal, leg?.arrival];
}

function airlineFromLeg(leg) {
  if (!leg || typeof leg !== "object") return "";
  return pickStr(
    leg.airline?.name,
    leg.airlineName,
    leg.marketingAirline?.name,
    leg.operatingAirline?.name,
    leg.airline?.code && leg.airline?.name === undefined ? leg.airline.code : "",
    leg.carrierName
  );
}

function flightNumberFromLeg(leg) {
  if (!leg || typeof leg !== "object") return "";
  return pickStr(
    leg.flightNumber,
    leg.flightNum,
    leg.number,
    leg.marketingFlightNumber,
    leg.operatingFlightNumber,
    leg.supplierFlightId
  );
}

function airlineCodeFromLeg(leg) {
  if (!leg || typeof leg !== "object") return "";
  return pickStr(
    leg.airline?.code,
    leg.airlineId,
    leg.airlineCode,
    leg.carrierCode,
    leg.carrier,
    leg.marketingCarrier,
    leg.operatingCarrier
  ).toUpperCase();
}

/** Avoid showing booking-class + datetime junk as "flight number" (e.g. A108214/06/2026). */
export function formatFlightNumberForDisplay(leg) {
  if (!leg || typeof leg !== "object") return "";
  const carrier = airlineCodeFromLeg(leg);
  let raw = flightNumberFromLeg(leg);
  if (!raw) return carrier || "";

  raw = String(raw).trim();
  if (/\d{1,2}\/\d{1,2}\/\d{4}/.test(raw) || /\d{4}-\d{2}-\d{2}/.test(raw) || raw.includes("T")) {
    const alt = pickStr(leg.marketingFlightNumber, leg.operatingFlightNumber, leg.flightNum);
    raw = alt || raw;
  }

  const slashSplit = raw.match(/^([A-Z]?\d{1,4})\/\d{1,2}\/\d{1,2}\/\d{4}/i);
  if (slashSplit?.[1]) raw = slashSplit[1];

  const digitsOnly = raw.match(/(\d{1,4})/);
  const num = digitsOnly?.[1] || raw.replace(/[^\dA-Z]/gi, "").slice(0, 6);
  if (carrier && num) {
    const stripped = String(num).replace(new RegExp(`^${carrier}`, "i"), "");
    return `${carrier}${stripped || num}`;
  }
  return raw.split(/[\s/]/)[0] || "";
}

function buildFlightRowsFromReservationOnly(order) {
  const or = safeOrderResponseObject(order);
  const reservation = or?.reservation && typeof or.reservation === "object" ? or.reservation : null;
  const fbFlights = flightArrayFromReservation(reservation);
  if (!fbFlights.length) return [];

  const directionLabels = ["הלוך", "חזור"];
  const rows = [];

  fbFlights.forEach((item, idx) => {
    const legs = legsFromFlightItem(item);
    const { first, last } = firstLastFromLegs(legs.filter(Boolean));
    if (!first && !last) return;

    const depC = depCodeFromLeg(first) || depCodeFromLeg(last);
    const arrC = arrCodeFromLeg(last) || arrCodeFromLeg(first);
    const depDateRaw =
      depDateTimeCandidates(first).find(Boolean) ||
      (idx === 0 ? order?.dep_date : null) ||
      (idx === 1 ? order?.ret_date : null);
    const arrLeg = last || first;
    const arrDateRaw = depDateTimeCandidates(arrLeg).find(Boolean);

    rows.push({
      direction: directionLabels[idx] || `טיסה ${idx + 1}`,
      depCode: depC,
      arrCode: arrC,
      depDate: formatDateHe(depDateRaw),
      depTime: formatTimeHe(depDateRaw),
      arrTime: formatTimeHe(arrDateRaw),
      airline: airlineFromLeg(first) || airlineFromLeg(last) || airlineCodeFromLeg(first),
      airlineCode: airlineCodeFromLeg(first) || airlineCodeFromLeg(last),
      flightNumber: formatFlightNumberForDisplay(first) || formatFlightNumberForDisplay(last),
      classCode: classFromLeg(first) || classFromLeg(last),
      baggageText: baggageFromLeg(first) || baggageFromLeg(last),
    });
  });

  return rows;
}

function classFromLeg(leg) {
  if (!leg || typeof leg !== "object") return "";
  return pickStr(leg.classOfService, leg.cabin, leg.bookingClass, leg.cabinClass, leg.serviceClass);
}

function baggageFromLeg(leg) {
  if (!leg || typeof leg !== "object") return "";
  const b = leg.baggageAllowance ?? leg.baggage ?? leg.checkedBaggage;
  if (typeof b === "string") return b.trim();
  if (b && typeof b === "object") {
    return pickStr(b.text, b.description, b.allowance, b.pieces != null ? `${b.pieces} כבודה` : "");
  }
  return "";
}

/**
 * @param {Record<string, unknown>} order — lds_orders row (+ optional order_response)
 * @returns {Array<{
 *   direction: string,
 *   depCode: string,
 *   arrCode: string,
 *   depDate: string,
 *   depTime: string,
 *   arrTime: string,
 *   airline: string,
 *   flightNumber: string,
 *   classCode: string,
 *   baggageText: string,
 * }>}
 */
export function normalizeLdsOrderLinkedFlights(order) {
  const selectedOption = getSelectedFlightOptionFromOrder(order);
  const selectedLf =
    selectedOption?.linkedFlights && typeof selectedOption.linkedFlights === "object"
      ? selectedOption.linkedFlights
      : null;
  const lf = selectedLf || getLdsOrderLinkedFlightsFromRow(order);
  logOrderDetailsFlightSsotTrace(
    order,
    selectedLf ? "selectedFlightOptionFromUser.linkedFlights" : "linkedflights.fallback"
  );
  if (!selectedLf && !hasNonEmptyLdsOrderLinkedFlightsBlob(order)) return [];

  const or = safeOrderResponseObject(order);
  const reservation = or?.reservation && typeof or.reservation === "object" ? or.reservation : null;
  const fbFlights = flightArrayFromReservation(reservation);

  const linkedItems = flightArrayFromLinked(lf);
  let items = linkedItems;
  if (!items.length && lf && Object.keys(lf).length > 0 && fbFlights.length > 0) {
    items = fbFlights;
  }
  if (!items.length) return [];

  const directionLabels = ["הלוך", "חזור"];

  const rows = [];
  items.forEach((item, idx) => {
    const fbItem = fbFlights[idx] || null;
    const legsPrimary = legsFromFlightItem(item);
    const legsFb = fbItem ? legsFromFlightItem(fbItem) : [];
    const mergedLegs = [];
    const n = Math.max(legsPrimary.length, legsFb.length, 1);
    for (let i = 0; i < n; i += 1) {
      mergedLegs.push(mergeLeg(legsPrimary[i], legsFb[i]));
    }
    const { first, last } = firstLastFromLegs(mergedLegs.filter(Boolean));
    if (!first && !last) return;

    const depC = depCodeFromLeg(first) || depCodeFromLeg(last);
    const arrC = arrCodeFromLeg(last) || arrCodeFromLeg(first);
    if (!depC && !arrC) return;

    const depDateRaw = depDateTimeCandidates(first)[0];
    const arrLeg = last || first;
    const arrDateRaw = arrDateTimeCandidates(arrLeg)[0];

    rows.push({
      direction: directionLabels[idx] || `טיסה ${idx + 1}`,
      depCode: depC,
      arrCode: arrC,
      depDate: formatDateHe(depDateRaw),
      depTime: formatTimeHe(depDateRaw),
      arrTime: formatTimeHe(arrDateRaw),
      airline: airlineFromLeg(first) || airlineFromLeg(last),
      airlineCode: airlineCodeFromLeg(first) || airlineCodeFromLeg(last),
      flightNumber:
        formatFlightNumberForDisplay(first) ||
        formatFlightNumberForDisplay(last) ||
        flightNumberFromLeg(first) ||
        flightNumberFromLeg(last),
      classCode: classFromLeg(first) || classFromLeg(last),
      baggageText: baggageFromLeg(first) || baggageFromLeg(last),
    });
  });

  return rows;
}

/**
 * Flight rows for order-details "פרטי טיסה" — linkedFlights SSOT first, reservation fallback.
 * @param {Record<string, unknown>|null|undefined} order
 * @returns {{ rows: Array, source: "linked"|"reservation"|"none" }}
 */
export function resolveLdsOrderDetailsFlightRows(order) {
  const linked = normalizeLdsOrderLinkedFlights(order);
  if (linked.length > 0) {
    return { rows: linked, source: "linked" };
  }
  const reservation = buildFlightRowsFromReservationOnly(order);
  if (reservation.length > 0) {
    return { rows: reservation, source: "reservation" };
  }
  return { rows: [], source: "none" };
}
